import type { SettingGroupDefinition } from "../../settings-catalog/types";
import {
  RETURNS_AUTOMATION_CODE_FUNCTIONS_GROUP_ID,
  RETURNS_AUTOMATIONS_PROCESSING_SETTINGS_PATH,
  RETURNS_AUTOMATIONS_SETTINGS_PATH,
  RETURNS_COMPENSATION_FLOW_EDITOR_SETTINGS_PATH,
  RETURNS_RESTOCKING_FLOW_EDITOR_SETTINGS_PATH,
} from "./settings-paths";
import {
  automationMoneyRisk,
  customerFacingRisk,
  exchangeSettingsOwner,
  internalDisplayRisk,
  requiresApproval,
  returnsAccess,
  returnsAssessmentsOwner,
  returnsFlowOwner,
  returnsManagedReturnsOwner,
  returnsSettingsOwner,
  returnsUiOwner,
  shippingAccess,
  suggestOnly,
  withdrawalsAccess,
} from "./shared";

export type ReturnsFlatTeamSettingGroupId =
  | "returns.ui_return_details"
  | "returns.ui_item_disposition"
  | "returns.ui_barcodes"
  | "returns.ui_gift_receipts"
  | "returns.ui_external_ra_number"
  | "returns.ui_return_editing"
  | "returns.pos"
  | "returns.pos_integration_locations"
  | "returns.processing"
  | "returns.advanced_processing"
  | "returns.automation_return_processing"
  | "returns.automation_claim_processing"
  | "returns.automation_inventory"
  | "returns.automation_label_expiration"
  | "returns.automation_agent_review"
  | "returns.automation_merchant_created"
  | typeof RETURNS_AUTOMATION_CODE_FUNCTIONS_GROUP_ID
  | "returns.automation_general"
  | "returns.automation_green_returns"
  | "returns.automation_dropoff_processing"
  | "returns.automation_shopify"
  | "returns.exchanges_order_notes"
  | "returns.exchanges_shipping_name"
  | "returns.exchanges_fulfillment"
  | "returns.exchanges_fulfillment_regions"
  | "returns.exchanges_orders"
  | "returns.permissions"
  | "returns.shipping_label_combination"
  | "returns.shipping_dropoff_providers"
  | "returns.shipping_return_locations"
  | "returns.shipping_options"
  | "returns.withdrawals_general"
  | "returns.withdrawals_notifications"
  | "returns.withdrawals_auto_cancellation"
  | "returns.withdrawals_exclusions"
  | "returns.assessments"
  | "returns.exchanges_order_pricing"
  | "returns.flow_claims"
  | "returns.flow_claims_finalize"
  | "returns.flow_compensation"
  | "returns.flow_labels"
  | "returns.flow_location"
  | "returns.flow_restocking"
  | "returns.flow_return_processing"
  | "returns.flow_returns"
  | "returns.flow_returns_finalize"
  | "returns.flow_warranties"
  | "returns.flow_warranties_finalize"
  | "returns.flow_warranties_registration"
  | "returns.flow_withdrawals"
  | "returns.managed_returns_centers"
  | "returns.managed_returns_general"
  | "returns.refulfillment_shipping"
  | "returns.managed_returns_processing_flow"
  | "returns.leftover_exchange_value"
  | "returns.warehouse_grade_actions"
  | "returns.policies_workflows";

export const ReturnsFlatTeamSettingGroups = [
  {
    id: "returns.refulfillment_shipping",
    title: "Re-fulfillment shipping",
    description:
      "Configure customer shipping discounts when Redo and a merchant fulfill separate parts of an order.",
    productArea: "returns",
    access: {
      read: { minVisibility: "admin" },
      write: {
        mode: "none",
        reason:
          "Configure in Admin Managed Returns. The dedicated save validates and publishes the Shopify discount.",
      },
    },
    owner: {
      kind: "teamSettings",
      path: "settings.reFulfillment.splitShipmentDiscount",
      product: "returns",
    },
    risk: {
      level: "high",
      reason: "Changes shipping charges paid by customers at checkout.",
    },
    agentEditability: {
      mode: "suggestOnly",
      reason:
        "Requires verified rate identities and Shopify Function publication through re-fulfillment settings.",
    },
    availability: {
      conditions: [
        {
          type: "evaluator",
          evaluator: "integration.connected",
          integration: "shopify",
        },
      ],
      unavailableBehavior: "hidden",
    },
  },
  {
    id: "returns.ui_return_details",
    title: "Return Details Display",
    description:
      "What Redo shows on a return in the merchant app: which line-item details appear, which product and variant metafields are surfaced, and whether the details and AI review sections start collapsed.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsUiOwner,
    risk: internalDisplayRisk,
    agentEditability: requiresApproval,
    fullPageTarget: { type: "settings", path: "returns-ui" },
    synonyms: [
      "return details",
      "line item details",
      "return metafields",
      "product metafields on returns",
      "collapse return details",
      "collapse AI review",
    ],
  },
  {
    id: "returns.ui_item_disposition",
    title: "Item Disposition",
    description:
      "Grade and outcome options warehouse staff pick from when dispositioning a returned item.",
    productArea: "returns",
    access: returnsAccess,
    owner: {
      kind: "teamSettings",
      path: "settings.returnItemDisposition",
      product: "returns",
    },
    risk: internalDisplayRisk,
    agentEditability: requiresApproval,
    fullPageTarget: { type: "settings", path: "returns-ui" },
    synonyms: [
      "item disposition",
      "return grades",
      "grade options",
      "outcome options",
      "disposition returned items",
    ],
  },
  {
    id: "returns.ui_barcodes",
    title: "Return Barcodes",
    description:
      "Barcode label size and the text printed above and below the barcode when processing returns.",
    productArea: "returns",
    access: shippingAccess,
    owner: {
      kind: "teamSettings",
      path: "settings.products.barcode",
      product: "returns",
    },
    risk: internalDisplayRisk,
    agentEditability: requiresApproval,
    fullPageTarget: { type: "settings", path: "shipping" },
    synonyms: [
      "scan returned items",
      "scan items when they come back",
      "scan returns",
      "barcodes",
      "barcode size",
      "barcode label",
      "print barcodes",
      "barcode text",
    ],
    changeGuidance: {
      summary:
        "Barcode dimensions must match the label stock in the warehouse — confirm the physical label size before changing.",
    },
  },
  {
    id: "returns.ui_gift_receipts",
    title: "Gift Receipts",
    description:
      "Whether gift recipients can start a return without the original order, the instructions they see when buyer information is missing, and whether merchants are notified of gift receipt requests.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsSettingsOwner,
    risk: customerFacingRisk,
    agentEditability: requiresApproval,
    fullPageTarget: { type: "settings", path: "returns-ui" },
    synonyms: [
      "gift receipts",
      "gift returns",
      "return a gift",
      "gift recipient return",
      "missing buyer info",
    ],
  },
  {
    id: "returns.ui_external_ra_number",
    title: "External RA Number",
    description:
      "Whether the external RA (return authorization) number from a connected system is shown on the return.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsUiOwner,
    risk: internalDisplayRisk,
    agentEditability: requiresApproval,
    fullPageTarget: { type: "settings", path: "returns-ui" },
    synonyms: [
      "external RA number",
      "RA number",
      "return authorization number",
      "external id",
    ],
  },
  {
    id: "returns.ui_return_editing",
    title: "Return Editing",
    description:
      "Which changes customers are allowed to make to a return after submitting it, such as cancelling it or editing the exchange shipping address.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsSettingsOwner,
    risk: customerFacingRisk,
    agentEditability: requiresApproval,
    fullPageTarget: { type: "settings", path: "returns-ui" },
    synonyms: [
      "return editing",
      "cancel return",
      "customer cancel return",
      "edit exchange address",
      "change return after submitting",
    ],
  },
  {
    id: "returns.pos",
    title: "Point of Sale",
    description:
      "Controls which financial adjustments store staff can apply while creating returns and exchanges in Shopify POS, plus the defaults applied to a POS return: whether items are restocked by default and which emails the customer receives.",
    productArea: "returns",
    access: returnsAccess,
    owner: { kind: "teamSettings", path: "settings.pos", product: "returns" },
    risk: {
      level: "high",
      reason:
        "Controls whether store staff can change the amount a customer receives or owes during an in-person return or exchange.",
    },
    agentEditability: requiresApproval,
    availability: {
      conditions: [
        {
          type: "evaluator",
          evaluator: "integration.connected",
          integration: "shopify",
        },
      ],
      unavailableBehavior: "hidden",
    },
    fullPageTarget: { type: "settings", path: "pos" },
    synonyms: [
      "point of sale",
      "POS returns",
      "POS exchanges",
      "in-store returns",
      "in store returns",
      "fees and bonuses",
      "manual exchange discounts",
      "POS restock",
      "POS emails",
    ],
  },
  {
    id: "returns.pos_integration_locations",
    title: "POS Integration Locations",
    description:
      "Maps each Shopify POS store location to the location an ERP or 3PL integration should use, so records created for an in-store return land at the store the customer returned to.",
    productArea: "returns",
    access: returnsAccess,
    owner: { kind: "teamSettings", path: "settings.pos", product: "returns" },
    risk: {
      level: "medium",
      reason:
        "Routes in-store returns to the wrong warehouse or store in the merchant's ERP when mapped incorrectly.",
    },
    agentEditability: requiresApproval,
    availability: {
      conditions: [
        {
          type: "evaluator",
          evaluator: "integration.connected",
          integration: "NetSuite",
        },
      ],
      unavailableBehavior: "hidden",
    },
    fullPageTarget: { type: "settings", path: "pos/integration-locations" },
    synonyms: ["integration locations", "POS integration locations"],
  },
  {
    id: "returns.policies_workflows",
    title: "Policies & Workflows",
    description:
      "The return, claim, warranty and withdrawal flows a shopper is taken through, and the fee charged on top of a customer-paid label.",
    productArea: "returns",
    /** The label fee charges the customer, so edits need shipping access. */
    access: shippingAccess,
    owner: returnsSettingsOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: { type: "settings", path: "customer-portal" },
    synonyms: [
      "policies and workflows",
      "return flows",
      "claim flow",
      "warranty flow",
      "withdrawal flow",
      "customer label fee",
    ],
    changeGuidance: {
      summary:
        "Editing a flow changes what every shopper is shown when they start a return — publish only after previewing it.",
    },
  },
  {
    id: "returns.processing",
    title: "Refunds & Store Credit",
    description:
      "How store credit is issued when a return is processed, and processing behaviors such as decoupling restock and checking for TryNow tags.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsSettingsOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: { type: "settings", path: "processing" },
    synonyms: [
      "return processing",
      "refunds and store credit",
      "store credit type",
      "gift card",
      "discount code",
      "refund settings",
      "how store credit is issued",
    ],
    changeGuidance: {
      summary:
        "Changing the store credit type changes how every future refund is paid out — confirm the destination system is connected first.",
    },
  },
  {
    id: "returns.advanced_processing",
    title: "Advanced Processing Flows",
    description:
      "Whether published Compensation and Restocking flows replace standard return processing and inventory automation.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsSettingsOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: {
      type: "settings",
      path: RETURNS_AUTOMATIONS_PROCESSING_SETTINGS_PATH,
    },
    synonyms: [
      "advanced processing flows",
      "advanced compensation processing",
      "advanced restocking flow",
    ],
  },
  {
    id: "returns.exchanges_order_notes",
    title: "Exchange Order Notes",
    description:
      "The notes Redo writes onto exchange orders, including whether to add the date, keep the existing note, and note the original order.",
    productArea: "returns",
    access: returnsAccess,
    owner: exchangeSettingsOwner,
    risk: internalDisplayRisk,
    agentEditability: requiresApproval,
    fullPageTarget: { type: "settings", path: "exchanges" },
    synonyms: [
      "exchange notes",
      "exchange order notes",
      "note on exchange order",
      "original order note",
    ],
  },
  {
    id: "returns.exchanges_shipping_name",
    title: "Exchange Shipping Name",
    description:
      "The shipping method name shown to customers on exchange orders.",
    productArea: "returns",
    access: returnsAccess,
    owner: exchangeSettingsOwner,
    risk: customerFacingRisk,
    agentEditability: requiresApproval,
    fullPageTarget: { type: "settings", path: "exchanges" },
    synonyms: [
      "exchange shipping name",
      "shipping method name",
      "exchange shipping label name",
    ],
  },
  {
    id: "returns.exchanges_fulfillment",
    title: "Exchange Fulfillment",
    description:
      "When exchange orders are created and fulfilled, including instant exchanges, hold amounts, expiration, and inventory minimums.",
    productArea: "returns",
    access: returnsAccess,
    owner: exchangeSettingsOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: { type: "settings", path: "exchanges" },
    synonyms: [
      "instant exchange",
      "advanced exchange",
      "exchange fulfillment",
      "hold amount",
      "exchange expiration",
      "inventory minimum",
    ],
    changeGuidance: {
      summary:
        "Instant and advanced exchanges ship before the return arrives — confirm the merchant accepts the inventory exposure.",
    },
  },
  {
    id: "returns.exchanges_fulfillment_regions",
    title: "Exchange Fulfillment Regions",
    description:
      "Which fulfillment locations serve exchange orders for each country, and the default locations when no region matches.",
    productArea: "returns",
    access: returnsAccess,
    owner: exchangeSettingsOwner,
    risk: customerFacingRisk,
    agentEditability: requiresApproval,
    fullPageTarget: { type: "settings", path: "exchanges" },
    synonyms: [
      "fulfillment regions",
      "exchange locations",
      "country locations",
      "region mapping",
      "default location",
      "ship exchanges from a different warehouse",
      "warehouse by country",
      "which warehouse ships the exchange",
    ],
  },
  {
    id: "returns.exchanges_orders",
    title: "Exchange Orders",
    description:
      "How exchange orders are named and priced: order name prefix and suffix, discount handling, variant picker behavior, fees, and tax treatment.",
    productArea: "returns",
    access: returnsAccess,
    owner: exchangeSettingsOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: { type: "settings", path: "exchanges" },
    synonyms: [
      "exchange order name",
      "order prefix",
      "order suffix",
      "exchange discounts",
      "variant picker",
      "exchange fees",
      "exchange tax",
    ],
  },
  {
    id: "returns.permissions",
    title: "Merchant-Created Returns Permissions",
    description:
      'What users with the base "user" role are allowed to do when creating or editing returns: which compensation methods they can choose, and whether they can edit fees, green returns, or the return itself.',
    productArea: "returns",
    access: returnsAccess,
    owner: {
      kind: "teamSettings",
      path: "settings.returns.permissions",
      product: "returns",
    },
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: { type: "settings", path: "permissions" },
    synonyms: [
      "give money back",
      "money back",
      "return permissions",
      "merchant created returns",
      "who can refund",
      "user role permissions",
      "allow refund",
      "edit return permission",
      "agent permissions",
    ],
    changeGuidance: {
      summary:
        "These control which staff can move money on a return — widen them only with the merchant's explicit approval.",
    },
  },
  {
    id: "returns.shipping_label_combination",
    title: "Return Label Combination",
    description:
      "Whether Redo combines multiple returned items onto one shipping label, and whether it verifies the merchant address before buying labels.",
    productArea: "returns",
    access: shippingAccess,
    owner: returnsSettingsOwner,
    risk: internalDisplayRisk,
    agentEditability: requiresApproval,
    fullPageTarget: { type: "settings", path: "shipping" },
    synonyms: [
      "combine labels",
      "sku grouping",
      "one label per return",
      "verify address for labels",
    ],
  },
  {
    id: "returns.shipping_dropoff_providers",
    title: "Dropoff Providers",
    description:
      "Which third-party dropoff networks customers can use to return packages without printing a label, and how far Redo will look for a dropoff location.",
    productArea: "returns",
    access: shippingAccess,
    owner: returnsSettingsOwner,
    risk: customerFacingRisk,
    agentEditability: requiresApproval,
    fullPageTarget: { type: "settings", path: "shipping" },
    synonyms: [
      "dropoff providers",
      "happy returns",
      "label free returns",
      "return bar",
      "dropoff network",
    ],
  },
  {
    id: "returns.shipping_return_locations",
    title: "Return Locations",
    description:
      "Where customers can return items in person, including which store locations accept returns and how far Redo will look for one.",
    productArea: "returns",
    access: shippingAccess,
    owner: {
      kind: "teamSettings",
      path: "settings.locations",
      product: "returns",
    },
    risk: customerFacingRisk,
    agentEditability: requiresApproval,
    fullPageTarget: { type: "settings", path: "shipping" },
    synonyms: [
      "return in store",
      "in store returns",
      "return locations",
      "store dropoff",
      "return radius",
    ],
  },
  {
    id: "returns.shipping_options",
    title: "Return Shipping Options",
    description:
      "Packing slips, who pays for the return label, customer label fees, carrier price caps, signature requirements, and the custom text printed on labels.",
    productArea: "returns",
    access: shippingAccess,
    owner: returnsSettingsOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: { type: "settings", path: "shipping" },
    synonyms: [
      "packing slip",
      "label fee",
      "who pays for return shipping",
      "deduct label cost",
      "signature required",
      "label text",
      "max label price",
    ],
    changeGuidance: {
      summary:
        "Label-fee and deduction settings change what the customer is charged and what the merchant absorbs — confirm before changing.",
    },
  },
  {
    id: "returns.withdrawals_general",
    title: "Withdrawals",
    description:
      "Whether customers can withdraw (cancel) an order before it ships, and how shipping is refunded when they do.",
    productArea: "returns",
    access: withdrawalsAccess,
    owner: {
      kind: "teamSettings",
      path: "settings.withdrawals",
      product: "returns",
    },
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: { type: "settings", path: "withdrawals" },
    synonyms: [
      "withdrawals",
      "cancel order",
      "order cancellation",
      "withdraw an order",
      "shipping refund",
    ],
  },
  {
    id: "returns.withdrawals_notifications",
    title: "Withdrawal Notifications",
    description:
      "Who is emailed when a withdrawal request is submitted, including merchant staff and third-party fulfillment contacts.",
    productArea: "returns",
    access: withdrawalsAccess,
    owner: {
      kind: "teamSettings",
      path: "settings.withdrawals",
      product: "returns",
    },
    risk: internalDisplayRisk,
    agentEditability: requiresApproval,
    fullPageTarget: { type: "settings", path: "withdrawals" },
    synonyms: [
      "withdrawal emails",
      "notify on withdrawal",
      "3PL notification",
      "fulfillment contacts",
    ],
  },
  {
    id: "returns.withdrawals_auto_cancellation",
    title: "Withdrawal Auto-Cancellation",
    description:
      "Whether unfulfilled orders are cancelled automatically on withdrawal, how long Redo waits, and which order tags block it.",
    productArea: "returns",
    access: withdrawalsAccess,
    owner: {
      kind: "teamSettings",
      path: "settings.withdrawals",
      product: "returns",
    },
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: { type: "settings", path: "withdrawals" },
    synonyms: [
      "auto cancel",
      "cancellation delay",
      "prevent auto cancellation",
      "block cancellation tags",
    ],
    changeGuidance: {
      summary:
        "Auto-cancellation voids real orders — confirm the delay and blocking tags with the merchant before changing.",
    },
  },
  {
    id: "returns.withdrawals_exclusions",
    title: "Withdrawal Exclusions",
    description:
      "Collections, product tags, and variant metafields that make an item ineligible for withdrawal.",
    productArea: "returns",
    access: withdrawalsAccess,
    owner: {
      kind: "teamSettings",
      path: "settings.withdrawals",
      product: "returns",
    },
    risk: customerFacingRisk,
    agentEditability: requiresApproval,
    fullPageTarget: { type: "settings", path: "withdrawals" },
    synonyms: [
      "withdrawal exclusions",
      "not eligible for withdrawal",
      "exclude collections",
      "exclude product tags",
    ],
  },
  {
    id: "returns.warehouse_grade_actions",
    title: "Warehouse Grade Actions",
    description:
      "What the warehouse does with a returned item once a grade is assigned during processing: accept it, flag it for review, or reject it, and whether accepting also restocks it. The grade set itself is configured on the return detail view page.",
    productArea: "returns",
    access: returnsAccess,
    owner: {
      kind: "teamSettings",
      path: "settings.redoManagedReturns",
      product: "returns",
    },
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: { type: "settings", path: "warehouse" },
    synonyms: [
      "grade actions",
      "warehouse grades",
      "accept or reject grade",
      "restock by grade",
      "grade mappings",
      "what happens when a grade is assigned",
    ],
  },
  {
    id: "returns.automation_return_processing",
    title: "Return Processing Automation",
    description:
      "The return status at which exchanges, refunds, and store credit are automatically processed, and how long Redo waits after that status is reached.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsSettingsOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: {
      type: "settings",
      path: RETURNS_AUTOMATIONS_PROCESSING_SETTINGS_PATH,
    },
    availability: {
      conditions: [
        {
          type: "evaluator",
          evaluator: "capability.enabled",
          capability: "returns",
        },
      ],
      unavailableBehavior: "hidden",
      reason:
        "Return processing automation is only shown when returns are enabled.",
    },
    synonyms: [
      "auto process returns",
      "process on delivery",
      "process in transit",
      "processing delay",
      "when to refund",
      "when refunds auto-process",
      "when returns auto-process",
      "when are returns processed",
      "auto-process at status",
      "auto-processing status",
      "automatically process refunds",
    ],
    changeGuidance: {
      summary:
        "Processing earlier than Delivered pays customers before the item is back — confirm the merchant accepts that risk.",
    },
  },
  {
    id: "returns.automation_claim_processing",
    title: "Claim Processing Automation",
    description:
      "The claim status at which exchanges, refunds, and store credit are automatically processed, and how long Redo waits after that status is reached.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsSettingsOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: {
      type: "settings",
      path: RETURNS_AUTOMATIONS_PROCESSING_SETTINGS_PATH,
    },
    availability: {
      conditions: [
        {
          type: "evaluator",
          evaluator: "capability.enabled",
          capability: "packageProtection",
        },
      ],
      unavailableBehavior: "hidden",
      reason:
        "Claim processing automation is only shown when package protection is enabled.",
    },
    synonyms: [
      "auto process claims",
      "claim processing",
      "when to process a claim",
      "claim processing delay",
      "when claim refunds process",
      "when claims auto-process",
      "auto-process claims at status",
      "automatically process claims",
    ],
    changeGuidance: {
      summary:
        "Processing earlier than Delivered pays customers before the item is back — confirm the merchant accepts that risk.",
    },
  },
  {
    id: "returns.automation_inventory",
    title: "Return Restocking",
    description:
      "Which returned items are automatically restocked to inventory when a return is processed.",
    productArea: "returns",
    access: returnsAccess,
    owner: {
      kind: "teamSettings",
      path: "settings.inventory",
      product: "returns",
    },
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: {
      type: "settings",
      path: RETURNS_AUTOMATIONS_SETTINGS_PATH,
    },
    synonyms: [
      "restock",
      "restock returns",
      "automatic restocking",
      "restock damaged items",
      "inventory on return",
    ],
  },
  {
    id: "returns.automation_label_expiration",
    title: "Label Expiration",
    description:
      "How long a return shipping label stays valid, the reminder and expiration emails customers receive, and what happens when an expired label is used anyway.",
    productArea: "returns",
    access: returnsAccess,
    owner: {
      kind: "teamSettings",
      path: "settings.labelExpiration",
      product: "returns",
    },
    risk: customerFacingRisk,
    agentEditability: requiresApproval,
    fullPageTarget: {
      type: "settings",
      path: RETURNS_AUTOMATIONS_SETTINGS_PATH,
    },
    synonyms: [
      "label expiration",
      "return label expires",
      "expired label",
      "label reminder emails",
      "reopen return expired label",
    ],
  },
  {
    id: "returns.automation_agent_review",
    title: "Agent Review",
    description:
      "What Redo does with a return at each fraud risk level — flag it for review, hold it, or take no action.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsSettingsOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: {
      type: "settings",
      path: RETURNS_AUTOMATIONS_SETTINGS_PATH,
    },
    availability: {
      conditions: [
        { type: "evaluator", evaluator: "returns.agentReviewEnabled" },
      ],
      unavailableBehavior: "hidden",
      reason: "Agent review is only shown when agent review is enabled.",
    },
    synonyms: [
      "agent review",
      "risk level",
      "fraud review",
      "flag risky returns",
      "high risk returns",
    ],
  },
  {
    id: "returns.automation_merchant_created",
    title: "Merchant-Created Returns",
    description:
      "Whether merchant staff can create returns on a customer's behalf, who pays for return labels by default, and whether merchant-created claims require shipping.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsSettingsOwner,
    risk: customerFacingRisk,
    agentEditability: requiresApproval,
    fullPageTarget: {
      type: "settings",
      path: RETURNS_AUTOMATIONS_SETTINGS_PATH,
    },
    synonyms: [
      "merchant created returns",
      "create return for customer",
      "merchant return flow",
      "merchant return label payer",
      "staff created returns",
    ],
  },
  {
    id: RETURNS_AUTOMATION_CODE_FUNCTIONS_GROUP_ID,
    title: "Code functions",
    description:
      "Custom code for exchange order metadata and return bonuses or fees.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsSettingsOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: {
      type: "settings",
      path: RETURNS_AUTOMATIONS_SETTINGS_PATH,
    },
    synonyms: [
      "code functions",
      "custom code",
      "exchange order function",
      "return adjustment function",
      "order sync function",
      "order return info function",
      "redojs",
    ],
  },
  {
    id: "returns.automation_general",
    title: "General Return Automation",
    description:
      "The store credit amount below which refunds are waived, order tagging, and default shipping reimbursement.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsSettingsOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: {
      type: "settings",
      path: RETURNS_AUTOMATIONS_SETTINGS_PATH,
    },
    synonyms: [
      "waive refund",
      "return tag on order",
      "reimburse original shipping",
    ],
  },
  {
    id: "returns.automation_green_returns",
    title: "Green Return Automation",
    description:
      "Which green (keep-the-item) request types — customer- and merchant-created — are automatically processed.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsSettingsOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: {
      type: "settings",
      path: RETURNS_AUTOMATIONS_PROCESSING_SETTINGS_PATH,
    },
    synonyms: [
      "green returns",
      "keep the item",
      "auto process green returns",
      "no return necessary",
    ],
  },
  {
    id: "returns.automation_dropoff_processing",
    title: "Dropoff Auto-Processing",
    description:
      "Which compensation methods are automatically processed when a return is dropped off with a third-party provider and dropoff verification passes.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsSettingsOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: {
      type: "settings",
      path: RETURNS_AUTOMATIONS_PROCESSING_SETTINGS_PATH,
    },
    synonyms: [
      "dropoff auto processing",
      "auto process dropoff returns",
      "dropoff compensation methods",
      "smarthub auto process",
      "fedex easy returns auto process",
    ],
  },
  {
    id: "returns.automation_shopify",
    title: "Shopify Return Automation",
    description:
      "How Redo mirrors returns into Shopify: whether Shopify returns are created, how they are closed, restocking and refund behavior, duties and store credit handling, and order notes.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsSettingsOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: {
      type: "settings",
      path: RETURNS_AUTOMATIONS_SETTINGS_PATH,
    },
    availability: {
      conditions: [
        {
          type: "evaluator",
          evaluator: "integration.connected",
          integration: "shopify",
        },
      ],
      unavailableBehavior: "hidden",
      reason:
        "Shopify return automation is only shown when Shopify is connected.",
    },
    synonyms: [
      "shopify returns",
      "create shopify return",
      "close shopify return",
      "archive shopify order",
      "shopify restock",
      "duty drawback",
      "store credit expiration",
    ],
    changeGuidance: {
      summary:
        "These settings write to the merchant's Shopify store — confirm the intended Shopify-side outcome before changing.",
    },
  },
  {
    id: "returns.exchanges_order_pricing",
    title: "Exchange Order Pricing",
    description:
      "How exchange orders are priced when Redo creates its own exchange order — the financial strategy, tax treatment, and payment gateways applied to the order.",
    productArea: "returns",
    access: returnsAccess,
    owner: exchangeSettingsOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: { type: "settings", path: "exchanges" },
    synonyms: [
      "exchange pricing",
      "exchange order pricing",
      "exchange financial strategy",
      "upsell value",
      "full price exchange",
      "exchange payment gateways",
      "add tax to exchange",
      "exchange tax",
    ],
  },
  {
    id: "returns.managed_returns_general",
    title: "Managed Returns",
    description:
      "The Redo-managed returns overview: custom grade actions, grade-to-outcome mappings, and access to the returns-center configuration and warehouse processing flow.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsManagedReturnsOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: { type: "settings", path: "warehouse" },
    synonyms: [
      "managed returns",
      "redo managed returns",
      "returns centers",
      "warehouse returns",
      "grade actions",
      "grade mappings",
    ],
  },
  {
    id: "returns.managed_returns_centers",
    title: "Returns Centers",
    description:
      "Configuration for each returns center: refulfillment, consolidation ship-to address and schedule, processing fee, and external location-hub mapping.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsManagedReturnsOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: { type: "settings", path: "warehouse" },
    synonyms: [
      "returns center",
      "return center detail",
      "consolidation",
      "refulfillment",
      "external location hub",
    ],
  },
  {
    id: "returns.managed_returns_processing_flow",
    title: "Warehouse Processing Flow",
    description:
      "The node-graph editor that defines how a returned item is processed and inspected at the warehouse.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsFlowOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: { type: "settings", path: "warehouse/processing-flow" },
    synonyms: [
      "warehouse processing flow",
      "inspection flow",
      "processing canvas",
      "warehouse inspection steps",
    ],
  },
  {
    id: "returns.assessments",
    title: "Assessments",
    description:
      "Build and manage assessment templates — custom field forms (text, select, number, boolean, file, date) attached to a return type to collect information during the return or warehouse-inspection workflow. Groups scope assessments to product collections.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsAssessmentsOwner,
    risk: customerFacingRisk,
    agentEditability: requiresApproval,
    fullPageTarget: { type: "settings", path: "assessments" },
    synonyms: [
      "assessments",
      "assessment templates",
      "custom fields on returns",
      "return questions",
      "inspection forms",
      "assessment groups",
    ],
  },
  {
    id: "returns.flow_returns",
    title: "Return Flow",
    description:
      "The customer-portal return flow editor — the node graph a customer walks through when starting a return.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsFlowOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: { type: "settings", path: "customer-portal/flows/returns" },
    synonyms: [
      "return flow",
      "return flow editor",
      "customer return flow",
      "return portal flow",
      "start a return",
      "how customers start a return",
      "what customers see when starting a return",
      "steps customers go through to return",
    ],
  },
  {
    id: "returns.flow_returns_finalize",
    title: "Return Finalize Flow",
    description:
      "The customer-portal flow shown after a return is submitted, finalizing the return.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsFlowOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: {
      type: "settings",
      path: "customer-portal/flows/returns-finalize",
    },
    synonyms: [
      "return finalize flow",
      "finalize return",
      "return confirmation flow",
    ],
  },
  {
    id: "returns.flow_claims",
    title: "Claim Flow",
    description:
      "The customer-portal claim flow editor — the node graph a customer walks through when filing a package-protection claim.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsFlowOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: { type: "settings", path: "customer-portal/flows/claims" },
    synonyms: [
      "claim flow",
      "claim flow editor",
      "package protection claim flow",
    ],
  },
  {
    id: "returns.flow_claims_finalize",
    title: "Claim Finalize Flow",
    description:
      "The customer-portal flow shown after a claim is submitted, finalizing the claim.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsFlowOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: {
      type: "settings",
      path: "customer-portal/flows/claims-finalize",
    },
    synonyms: [
      "claim finalize flow",
      "finalize claim",
      "claim confirmation flow",
    ],
  },
  {
    id: "returns.flow_warranties",
    title: "Warranty Flow",
    description:
      "The customer-portal warranty flow editor — the node graph a customer walks through when filing a warranty request.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsFlowOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: {
      type: "settings",
      path: "customer-portal/flows/warranties",
    },
    synonyms: [
      "warranty flow",
      "warranty flow editor",
      "warranty request flow",
    ],
  },
  {
    id: "returns.flow_warranties_finalize",
    title: "Warranty Finalize Flow",
    description:
      "The customer-portal flow shown after a warranty request is submitted, finalizing the warranty.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsFlowOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: {
      type: "settings",
      path: "customer-portal/flows/warranties-finalize",
    },
    synonyms: [
      "warranty finalize flow",
      "finalize warranty",
      "warranty confirmation flow",
    ],
  },
  {
    id: "returns.flow_warranties_registration",
    title: "Warranty Registration Flow",
    description:
      "The customer-portal flow a customer walks through to register a product for warranty coverage.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsFlowOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: {
      type: "settings",
      path: "customer-portal/flows/warranties-registration",
    },
    synonyms: [
      "warranty registration flow",
      "register product",
      "product registration flow",
    ],
  },
  {
    id: "returns.flow_withdrawals",
    title: "Withdrawal Flow",
    description:
      "The customer-portal withdrawal flow editor — the node graph a customer walks through when withdrawing an order before it ships.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsFlowOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: {
      type: "settings",
      path: "customer-portal/flows/withdrawals",
    },
    synonyms: [
      "withdrawal flow",
      "withdrawal flow editor",
      "cancel order flow",
    ],
  },
  {
    id: "returns.flow_location",
    title: "Return Location Flow",
    description:
      "The flow editor that decides which return location a customer is routed to.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsFlowOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: { type: "settings", path: "shipping/location-flow-editor" },
    synonyms: [
      "location flow",
      "return location flow",
      "location routing flow",
    ],
  },
  {
    id: "returns.flow_labels",
    title: "Return Labels Flow",
    description:
      "The flow editor that decides which return shipping label a customer receives.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsFlowOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: { type: "settings", path: "shipping/labels-flow-editor" },
    synonyms: ["labels flow", "return label flow", "label routing flow"],
  },
  {
    id: "returns.flow_compensation",
    title: "Compensation Flow",
    description:
      "The advanced-flow editor that decides how a return is compensated — refund, store credit, or exchange.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsFlowOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: {
      type: "settings",
      path: RETURNS_COMPENSATION_FLOW_EDITOR_SETTINGS_PATH,
    },
    synonyms: [
      "compensation flow",
      "compensation flow editor",
      "how returns are compensated",
    ],
  },
  {
    id: "returns.flow_restocking",
    title: "Restocking Flow",
    description:
      "The advanced-flow editor that decides how returned items are restocked to inventory.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsFlowOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: {
      type: "settings",
      path: RETURNS_RESTOCKING_FLOW_EDITOR_SETTINGS_PATH,
    },
    synonyms: [
      "restocking flow",
      "restock flow editor",
      "restock automation flow",
    ],
  },
  {
    id: "returns.flow_return_processing",
    title: "Return Processing Flow",
    description:
      "The advanced-flow editor that defines how a return is processed automatically.",
    productArea: "returns",
    access: returnsAccess,
    owner: returnsFlowOwner,
    risk: automationMoneyRisk,
    agentEditability: suggestOnly,
    fullPageTarget: {
      type: "settings",
      path: RETURNS_AUTOMATIONS_PROCESSING_SETTINGS_PATH,
    },
    synonyms: [
      "return processing flow",
      "processing flow editor",
      "return automation flow",
    ],
  },
  {
    id: "returns.leftover_exchange_value",
    title: "Exchange value price difference & out-of-stock policy",
    description:
      "Choose how customers are compensated when an exchange costs less than the original item or the replacement item is unavailable.",
    productArea: "returns",
    access: returnsAccess,
    owner: exchangeSettingsOwner,
    risk: customerFacingRisk,
    agentEditability: requiresApproval,
    fullPageTarget: { type: "settings", path: "exchanges" },
    synonyms: [
      "exchange remainder",
      "leftover exchange credit",
      "exchange refund choice",
      "shopper exchange payout choice",
    ],
    changeGuidance: {
      summary:
        "Confirm the shopper choices and bonus or fee amount before changing these settings.",
    },
  },
] as const satisfies readonly SettingGroupDefinition<ReturnsFlatTeamSettingGroupId>[];

type _assertAllReturnsFlatTeamSettingGroupIdsProvided =
  ReturnsFlatTeamSettingGroupId extends (typeof ReturnsFlatTeamSettingGroups)[number]["id"]
    ? true
    : never;
const _allReturnsFlatTeamSettingGroupIdsProvided: _assertAllReturnsFlatTeamSettingGroupIdsProvided = true;
void _allReturnsFlatTeamSettingGroupIdsProvided;

export const ReturnsFlatTeamSettingGroupById: Record<
  ReturnsFlatTeamSettingGroupId,
  SettingGroupDefinition<ReturnsFlatTeamSettingGroupId>
> = (() => {
  const map: Partial<
    Record<
      ReturnsFlatTeamSettingGroupId,
      SettingGroupDefinition<ReturnsFlatTeamSettingGroupId>
    >
  > = {};
  for (const group of ReturnsFlatTeamSettingGroups) {
    map[group.id] = group;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return map as Record<
    ReturnsFlatTeamSettingGroupId,
    SettingGroupDefinition<ReturnsFlatTeamSettingGroupId>
  >;
})();
